import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  alpha,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Container,
  Card
} from "@mui/material";
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon
} from "@mui/icons-material";
import CustomSnackbarAlert from "../components/customAlert";
import UserFormDialog from "../components/UserFormDialog";
import { fetchAllUsers, deleteUser } from "../api/usersApi";

export default function Users() {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [createOrUpdate, setCreateOrUpdate] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);

  // for Alert
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showAlert, setShowAlert] = useState(false);

  const getAllUsers = async () => {
    setLoading(true);
    try {
      const data = await fetchAllUsers();
      if (data.status) {
        setUsers(data.data);
      } else {
        setError(data.error);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseSnackbar = () => {
    setShowAlert(false);
  };

  const handleSuccess = (mes) => {
    setOpenDialog(false);
    setAlertMessage(mes);
    setAlertSeverity("success");
    setShowAlert(true);
    getAllUsers();
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const data = await deleteUser(userId);
        if (data.status) {
          setAlertMessage(data.message);
          setAlertSeverity("success");
          setShowAlert(true);
          getAllUsers();
        }
      } catch (e) {
        setAlertMessage("Failed to delete user");
        setAlertSeverity("error");
        setShowAlert(true);
      }
    }
  };

  const handleOpenDialogForCreateUser = () => {
    setCreateOrUpdate("create");
    setSelectedUser(null);
    setOpenDialog(true);
  };

  const handleOpenDialogForUpdate = (user) => {
    setCreateOrUpdate("update");
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'error';
      case 'editor': return 'warning';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage administrative access and roles
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialogForCreateUser}
          sx={{ borderRadius: 2, px: 3, boxShadow: theme.shadows[4] }}
        >
          Create User
        </Button>
      </Box>

      {error ? (
        <Typography color="error" textAlign="center">{error}</Typography>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { label: "User", icon: <PersonIcon sx={{ fontSize: '1rem' }} /> },
                    { label: "Email", icon: <EmailIcon sx={{ fontSize: '1rem' }} /> },
                    { label: "Role", icon: <BadgeIcon sx={{ fontSize: '1rem' }} /> },
                    { label: "Actions" }
                  ].map((column, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        fontWeight: 800,
                        backgroundColor: 'grey.50',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        {column.icon}
                        <span>{column.label}</span>
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{user.userName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        size="small"
                        color={getRoleColor(user.role)}
                        sx={{ fontWeight: 800, fontSize: '0.65rem', borderRadius: 1.5, textTransform: 'uppercase' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit User">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleOpenDialogForUpdate(user)}
                            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteUser(user._id)}
                            sx={{ bgcolor: alpha(theme.palette.error.main, 0.05), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <UserFormDialog
        createOrUpdate={createOrUpdate}
        onClose={handleCloseDialog}
        onSuccess={handleSuccess}
        open={openDialog}
        user={selectedUser}
      />
      
      <CustomSnackbarAlert
        message={alertMessage}
        severity={alertSeverity}
        open={showAlert}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

